import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CreateGames,
  checkCashUser,
  editGame,
  gameImageUpload,
  getVanityUrl,
} from "../../../../api/user";
import AssetSelector from "../AssetSelector";
import { baseSymbol } from "../../../../utils/instrumentFormat";
import { formatPrize, leagueDateRange, portfolioVisibilityShort } from "../leagueFormat";
import { stripHtml } from "../leagueStatus";
import { LeagueIdentityBlock } from "../../league/LeagueShell";
import { getLeagueIconKey, getLeagueMedia } from "../leagueImage";
import { refreshLeagueContext } from "../../../../utils/useLeagueContext";
import useWindowWidth from "../../../../hooks/useWindowWidth";
import ReviewRow from "./ReviewRow";
import WizardStepper from "./WizardStepper";
import WizardHeader, { HEADER_HEIGHT } from "./WizardHeader";
import {
  scrollToTop,
  useFocusedFlowChrome,
  useKeyboardAwareFocus,
} from "./wizardScroll";
import {
  Field,
  IconPicker,
  ImageUpload,
  InfoCard,
  LinkInput,
  RichTextArea,
  TextInput,
} from "./WizardFields";
import SettingsStep from "./SettingsStep";
import {
  ASSET_LABELS,
  ASSET_OPTIONS,
  EXPERIENCE_OPTIONS,
  FIELD_STEP,
  ICON_KEYS,
  LIMITS,
  STEPS,
  VARIANTS,
  emptyForm,
  slugify,
  validateStep,
} from "./leagueWizardConfig";
import {
  CalendarIcon,
  ChartFrameIcon,
  ClipboardIcon,
  CryptoIcon,
  EyeIcon,
  EyeOffIcon,
  ForexIcon,
  GlobeIcon,
  NetworkGlobeIcon,
  PeopleGroupIcon,
  PieMixedIcon,
  StocksIcon,
} from "../icons";

// The four options the picker offers. Simple placeholders by intent — better
// icon/image choices are a round-two decision. Keys map to
// public/league-icons/<key>.svg, which is what gets uploaded as the league image.
const ICON_COMPONENTS = {
  group: { label: "Group", Comp: PeopleGroupIcon },
  globe: { label: "Globe", Comp: GlobeIcon },
  chart: { label: "Chart", Comp: ChartFrameIcon },
  network: { label: "Network", Comp: NetworkGlobeIcon },
};

const ICON_OPTIONS = ICON_KEYS.map((key) => ({ key, ...ICON_COMPONENTS[key] }));

const ASSET_ICONS = {
  stocks: StocksIcon,
  crypto: CryptoIcon,
  forex: ForexIcon,
  mixed: PieMixedIcon,
};

const rasterizeIconToPng = (iconKey, size = 256) =>
  new Promise((resolve, reject) => {
    const url = `${process.env.PUBLIC_URL || ""}/league-icons/${iconKey}.svg`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#E6F2F0";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob returned null"));
          return;
        }
        resolve(
          new File([blob], `qushi-icon-${iconKey}-${Date.now()}.png`, {
            type: "image/png",
          })
        );
      }, "image/png");
    };
    img.onerror = () => reject(new Error(`Failed to load icon SVG: ${url}`));
    img.src = url;
  });

// How much empty space the scrolling content ends with while the sticky CTA is
// on screen. The bar is a 52px button in 12px of padding plus its border, so 120
// clears it with room to spare — the client's note: "Add enough bottom content
// inset so the final form content can scroll fully above the sticky CTA."
const STICKY_CTA_INSET = 120;

// Client Rev 1, Screen 2: the Advanced line "wraps the one word". The shorter
// copy the client supplied still needs two lines at 390px, so the last two words
// are bound together with a non-breaking space: the line still wraps, it just
// never strands a single word on a line of its own.
const noOrphan = (text) => (text || "").replace(/\s+(\S+)$/, "\u00A0$1");

// "Sep 1 to Dec 15" on Review — the same range the league's own cards will print
// once it is created, through the one shared helper rather than a second
// toLocaleDateString here. The year is dropped inside the current year on the
// client's instruction: "I don't think including the year is necessary until we
// are no longer in 2026."
const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  return leagueDateRange(startDate, endDate);
};

// What each step is called when the flow is editing rather than creating. The
// creation copy ("Start with the basics.") reads as first-time instructions.
const EDIT_STEP_SUBTITLES = {
  1: "Update the basics.",
  2: "Update the details.",
  3: "Update your league rules.",
  4: "Check it over and save.",
};

// The form as it stands for a league that already exists — every field the flow
// owns, read back off the record so an untouched input saves the value it had.
//
// Dates arrive as "YYYY-MM-DD HH:mm:ss" and the inputs are type=date, which only
// accepts "YYYY-MM-DD". Assets: an empty ticker list is how the API says "the
// whole category", which is this flow's "all" mode.
const seedForm = (variant, game) => {
  const base = emptyForm(variant);
  if (!game?.id) return base;

  const dateOnly = (v) => (v ? String(v).slice(0, 10) : "");
  const symbols = Array.isArray(game.asset_symbols)
    ? game.asset_symbols.map((a) => (typeof a === "string" ? a : a?.symbol)).filter(Boolean)
    : [];

  return {
    ...base,
    title: game.title || "",
    tagline: stripHtml(game.tagline || ""),
    description: game.description || "",
    icon: getLeagueIconKey(game) || base.icon,
    // The cover it already has, so the upload card shows the current art rather
    // than an empty dropzone. `imageFile` stays null: submit only re-uploads when
    // the organiser picks something new.
    imagePreview: getLeagueMedia(game).hasUploadedMedia ? getLeagueMedia(game).url : "",
    experienceLevel: game.experience_level || base.experienceLevel,
    vanityUrl: game.vanity_url || "",
    // Already named, so the slug must not be re-derived from the title on edit.
    vanityUrlTouched: true,
    startDate: dateOnly(game.start_date),
    endDate: dateOnly(game.end_date),
    assetCategory: game.asset_category || base.assetCategory,
    assetMode: symbols.length > 0 ? "custom" : "all",
    assetSymbols: symbols,
    joinAfterStart: !!game.join_after_start,
    privatePortfolio: !!game.private_portfolio,
    reward: game.reward ? stripHtml(String(game.reward)).trim() : "",
    cashLeague: !!game.is_cash_prize_league,
    cashPrizeAmount: game.cash_prize_amount ?? "",
    firstPlaceAmount: game.first_place_amount ?? "",
    secondPlaceAmount: game.second_place_amount ?? "",
    thirdPlaceAmount: game.third_place_amount ?? "",
  };
};

// ── Surviving the remount ─────────────────────────────────────────────────
//
// Client Rev 6, point 2: "If you are on Step 2, 3 or Step 4 and click the
// desktop browser Back button, it sends you all the way back to Step 1 instead
// of the previous step. It also clears all of the information you already
// entered."
//
// Rev 5 put the STEP into history state, and that part works — measured after a
// Back from Step 4, `history.state.wizardStep` reads 3, exactly as intended.
// What the client is seeing is the component being destroyed underneath it:
// measured on the real journey (League Play -> Create League link -> Next, Next,
// Next -> Back), the screen came back showing "Start with the basics." with an
// empty League name, while history state still said step 3. Fresh `useState`
// means step 1 and `seedForm()` means blank — i.e. a remount, not a navigation.
//
// The remount is the `ProtectedRoute`-declared-inside-`App` problem (App.jsx).
// Every App render creates a new component TYPE, so React throws away the whole
// subtree below it and builds a new one. popstate makes the router re-render,
// which re-renders App, which remounts this flow.
//
// It only shows up when the wizard was opened the way a real user opens it — by
// clicking through from League Play. Loading /contest/create-public-league
// directly does NOT reproduce it, which is why it survived the Rev 5 pass.
//
// Fixing ProtectedRoute would change how every route in the app mounts, and some
// screens rely on that remount to reset themselves. So the flow is made to
// survive instead: the draft lives in MODULE scope, which React cannot discard
// because it is not component state. A remount re-seeds from it and the
// organiser sees no interruption.
//
// Module memory (not sessionStorage) on purpose: `imageFile` is a File and
// `imagePreview` is an object URL, and neither survives being serialised. This
// keeps a picked cover image intact.
const WIZARD_DRAFTS = new Map();

const draftKeyFor = (variantKey, game) => `${variantKey}:${game?.id ?? "new"}`;

/**
 * Create League — one wizard for both the public and private flows.
 *
 * `variant` ("public" | "private") selects the copy, the league-link prefix and
 * the post-submit destination from leagueWizardConfig; everything else — inputs,
 * steps, validation, the stepper, Review & Create — is shared, so the two flows
 * cannot drift apart.
 */
const LeagueWizard = ({ variant: variantKey = "public", game = null }) => {
  const variant = VARIANTS[variantKey] || VARIANTS.public;
  const navigate = useNavigate();

  // Editing an existing league rather than creating one. Same four steps, same
  // fields, same validation — it opens on Settings (which is what "League
  // settings" in the league rail means), saves instead of creating, and returns
  // to the league.
  const isEdit = !!game?.id;
  const leagueHome = game?.vanity_url ? `/contest/${game.vanity_url}` : "/contest";

  // Which history entry this flow is sitting on. A REMOUNT keeps the same entry
  // (and so the same key); walking into the flow again from League Play pushes a
  // new one. That is the whole test for "is this the same visit?", so a draft is
  // only ever restored into the visit that created it and a fresh Create League
  // always opens empty.
  const locationKey = useLocation().key;
  const draftKey = draftKeyFor(variantKey, game);
  const restored =
    WIZARD_DRAFTS.get(draftKey)?.locationKey === locationKey
      ? WIZARD_DRAFTS.get(draftKey)
      : null;

  // WHICH step comes from history, because that is what the Back button just
  // decided and it is authoritative. The draft only supplies it when history has
  // nothing to say (a first mount).
  const [step, setStep] = useState(() => {
    const fromHistory = window.history.state?.wizardStep;
    if (typeof fromHistory === "number") return fromHistory;
    return restored?.step ?? (isEdit ? 3 : 1);
  });
  // Set when the user opened a step from Review & Create — saving or continuing
  // then brings them straight back to Review instead of walking the rest of the
  // flow again.
  const [returnToReview, setReturnToReview] = useState(
    () => restored?.returnToReview ?? false
  );
  const [submitting, setSubmitting] = useState(false);
  // THE DATA comes from the draft — this is the half the client is reporting as
  // "it clears all of the information you already entered".
  const [form, setForm] = useState(
    () => restored?.form ?? seedForm(variant, game)
  );
  const [errors, setErrors] = useState({});
  const [existingVanityUrls, setExistingVanityUrls] = useState([]);
  const [cashAllowed, setCashAllowed] = useState(false);
  const aborted = useRef(false);
  // The flow's own scroll root. Everything that repositions the screen measures
  // from here, because `.mainWrapper` (not the window) is what scrolls — see
  // wizardScroll.js.
  const containerRef = useRef(null);

  // THE focused-flow switch — and it is a BREAKPOINT switch, not a constant.
  //
  // Client Rev 3: "I went to the desktop version and just noticed that some
  // mobile features, like the sticky CTAs, and our mobile focused headers with
  // the screen title centered and a back chevron, are now seen on the desktop
  // version... Desktop has the native back button in the browser area. Our
  // desktop site rule is that screen titles should be left-aligned and share the
  // same starting margin as the content block... Desktop Create League uses the
  // normal QUSHi desktop shell. Mobile Create League uses the focused mobile
  // shell."
  //
  // Everything the focused shell owns hangs off this one flag, so the two
  // designs cannot drift apart:
  //   • WizardHeader (centred title + back chevron)   mobile only
  //   • the sticky bottom CTA                          mobile only
  //   • body.league-create-flow (chrome suppression)   mobile only
  //   • keyboard-aware field scrolling                 mobile only
  //   • the top/bottom padding those two reserve       mobile only
  // On desktop the same screens render a left-aligned title and an inline CTA
  // inside the ordinary app shell — DashboardHeader comes back via Contest.jsx,
  // which reads the same breakpoint.
  //
  // 768px is Tailwind's `md`, so this JS switch and every `md:` class on these
  // screens flip at exactly the same width.
  const focused = useWindowWidth() < 768;

  useFocusedFlowChrome(focused);
  useKeyboardAwareFocus(containerRef, {
    headerOffset: HEADER_HEIGHT,
    enabled: focused,
  });

  // Client Rev 1: "Every screen should open at the top of the page. Today it was
  // super buggy, with some screens 1-5 opening midway down." A fresh mount jumps
  // rather than animates — the user never chose the position it starts from.
  useEffect(() => {
    scrollToTop(containerRef.current);
  }, []);

  // Client Rev 2: "If you scroll down on screen three and then hit next. Screen
  // four does not start at the top of the screen. Please check this in the public
  // league flow as well. The key is to scroll down on screen 3 first, then
  // continue."
  //
  // The scroll used to run in the same tick as setStep. React batches state, so
  // it measured and animated against the step the user was LEAVING; the next
  // step then rendered underneath it and the in-flight SMOOTH animation was
  // abandoned wherever the content swap had left it — which is why it only shows
  // up once you have actually scrolled down first.
  //
  // Running it from an effect keyed on `step` means the new screen is already in
  // the DOM before anything is measured, and the move is instant: an animation
  // across a screen the user never sees buys nothing and is what made the
  // landing position unpredictable. The second pass on the next frame covers
  // Review, whose height settles after the league art paints.
  //
  // One wizard serves both flows, so this is the fix for public and private
  // alike.
  const lastStepRef = useRef(step);
  useEffect(() => {
    if (lastStepRef.current === step) return;
    lastStepRef.current = step;
    scrollToTop(containerRef.current);
    const raf = requestAnimationFrame(() => scrollToTop(containerRef.current));
    return () => cancelAnimationFrame(raf);
  }, [step]);

  useEffect(() => {
    aborted.current = false;
    (async () => {
      try {
        const res = await getVanityUrl();
        if (!aborted.current && res?.status === 200) {
          setExistingVanityUrls(res.data?.data || []);
        }
      } catch (e) {
        // non-fatal — the backend rejects a duplicate on submit anyway
      }
    })();

    if (variant.allowCashPrize) {
      (async () => {
        try {
          const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
          if (!userInfo?.id) return;
          const res = await checkCashUser({ id: userInfo.id });
          if (!aborted.current && res?.status === 200) {
            setCashAllowed(!!res.data?.status);
          }
        } catch (e) {
          // non-fatal — without permission the toggle stays hidden
        }
      })();
    }

    return () => {
      aborted.current = true;
    };
  }, [variant.allowCashPrize]);

  const vanityTaken = useMemo(() => {
    if (!form.vanityUrl) return false;
    // A league already owns its own slug, so seeing it in the taken list is not a
    // clash — without this every edit would open with "that link is taken".
    const own = (game?.vanity_url || "").toLowerCase();
    return (existingVanityUrls || []).some((entry) => {
      const v = typeof entry === "string" ? entry : entry?.vanity_url;
      if (!v) return false;
      const candidate = v.toLowerCase();
      if (own && candidate === own) return false;
      return candidate === form.vanityUrl.toLowerCase();
    });
  }, [existingVanityUrls, form.vanityUrl, game]);

  const vanitySuggestions = useMemo(() => {
    if (!vanityTaken || !form.vanityUrl) return [];
    return [1, 2, 3].map((n) => `${form.vanityUrl}-${n}`);
  }, [vanityTaken, form.vanityUrl]);

  // Cash-prize validation only applies where the flow allows it AND this user is
  // permitted to fund one.
  const cashEnabled = variant.allowCashPrize && cashAllowed;

  const experienceOption = EXPERIENCE_OPTIONS.find(
    (o) => o.key === form.experienceLevel
  );
  const experienceBlurb = experienceOption?.blurb || "";
  // Review shows the choice itself ("Beginner"), not the blurb that explains it.
  const experienceLabel = experienceOption?.label || "";

  const check = useCallback(
    (s) =>
      validateStep(s, form, {
        vanityTaken,
        variant: {
          allowCashPrize: cashEnabled,
          showReward: variant.showReward,
          showIconPicker: variant.showIconPicker,
        },
      }),
    [form, vanityTaken, cashEnabled, variant.showReward, variant.showIconPicker]
  );

  // ── The browser Back button walks the steps, like the in-flow back arrow ──
  //
  // Client Rev 5: "The browser Back button is not navigating between League
  // Creation steps correctly. For example, from Step 2 it should return to Step
  // 1, and from Step 4 it should return to Step 3. Currently, using the browser
  // Back button exits League Creation completely and returns the user to the
  // League Play Home screen."
  //
  // It did, and it had to: the step lived only in React state, so the whole
  // four-step flow was ONE history entry. Back popped that single entry and the
  // browser did the only thing it could — leave.
  //
  // So every step change also writes a history entry carrying the step it moved
  // to, and the popstate listener below reads the step back out. Back therefore
  // replays the steps the user visited: 4 -> 3 -> 2 -> 1, and one more Back from
  // Step 1 leaves the flow, which is what the in-flow arrow does at Step 1.
  //
  // ── Why the raw History API and not router navigate() ──
  //
  // The first version of this used `navigate(samePath, { state })`. That wiped
  // every field the organiser had typed: Review showed "—" for name, tagline,
  // link and dates, and stepping back showed empty inputs. Measured with a mount
  // counter, one Continue took the wizard from 2 mounts to 3 — the component was
  // being REMOUNTED, so `useState(() => seedForm(...))` re-seeded to blank.
  //
  // The remount is not this file's doing. `ProtectedRoute` is declared inside the
  // App component (App.jsx), so every App render creates a new function — a new
  // component TYPE — and React unmounts and remounts everything below it. Normal
  // navigation changes the path and remounts anyway, which is why nothing had
  // surfaced it before; a same-path navigate made it visible.
  //
  // Fixing that would mean changing how every route in the app is mounted, so it
  // is left alone. Writing to window.history directly keeps the router's location
  // unchanged — no router render, no App render, no remount — while still
  // producing the real history entries the Back button needs. The existing state
  // object is spread through so the router's own bookkeeping (usr/key/idx) is
  // preserved rather than overwritten.
  //
  // The URL is untouched either way: same path, same query. The step rides in
  // history STATE, never in the address bar.
  const pushStep = useCallback((target, { replace = false } = {}) => {
    const next = { ...(window.history.state || {}), wizardStep: target };
    if (replace) window.history.replaceState(next, "");
    else window.history.pushState(next, "");
  }, []);

  // The entry the flow opened on has no step of its own yet. Stamp it (replacing,
  // never pushing) so Step 1 sits on a real entry and the entry BEHIND it is
  // still League Play — the screen Back should land on from Step 1.
  useEffect(() => {
    if (window.history.state?.wizardStep == null) {
      pushStep(isEdit ? 3 : 1, { replace: true });
    }
    // Mount only: after this the step is always carried forward by pushStep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the module draft in step with what is on screen, so whatever moment the
  // remount happens in, the replacement mounts with the same content.
  useEffect(() => {
    WIZARD_DRAFTS.set(draftKey, { locationKey, step, form, returnToReview });
  }, [draftKey, locationKey, step, form, returnToReview]);

  // Back/Forward arrived: adopt whatever step that history entry carries. An
  // entry with no wizardStep is not ours — it is the screen the flow was opened
  // from — so it is left to the router to handle, which unmounts this flow.
  useEffect(() => {
    const onPop = () => {
      const fromHistory = window.history.state?.wizardStep;
      if (typeof fromHistory !== "number") return;
      setErrors({});
      setStep(fromHistory);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const goTo = useCallback(
    (target) => {
      setErrors({});
      setStep(target);
      pushStep(target);
    },
    [pushStep]
  );

  // Forward moves validate every step being crossed, so a stepper click can't
  // skip a required field. Backward moves are always free.
  const requestStep = (target) => {
    if (target <= step) {
      setReturnToReview(false);
      goTo(target);
      return;
    }
    for (let s = step; s < target; s += 1) {
      const e = check(s);
      if (Object.keys(e).length > 0) {
        setErrors(e);
        setStep(s);
        // Landed on an incomplete step: it gets its own entry, so Back from here
        // returns to the step before it rather than out of the flow.
        if (s !== step) pushStep(s);
        return;
      }
    }
    setReturnToReview(false);
    goTo(target);
  };

  const handleNext = () => {
    const e = check(step);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (returnToReview) {
      setReturnToReview(false);
      goTo(STEPS.length);
      return;
    }
    goTo(Math.min(STEPS.length, step + 1));
  };

  const handleBack = () => {
    setErrors({});
    if (returnToReview) {
      setReturnToReview(false);
      goTo(STEPS.length);
      return;
    }
    if (step === 1) {
      // Leaving the flow on purpose — the draft has served its purpose and must
      // not greet the organiser the next time they start a league.
      WIZARD_DRAFTS.delete(draftKey);
      navigate(isEdit ? leagueHome : "/contest");
      return;
    }
    goTo(step - 1);
  };

  // The preview is an object URL, so the old one has to be released whenever it's
  // replaced or cleared — otherwise picking four images leaks three of them.
  const pickImage = (file) => {
    setForm((prev) => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) };
    });
    setErrors((prev) => ({ ...prev, imageFile: undefined }));
  };

  const clearImage = () => {
    setForm((prev) => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, imageFile: null, imagePreview: "" };
    });
  };

  // Leaving the flow shouldn't leave the last preview URL alive. Read through a
  // ref so the cleanup frees whatever is current at unmount.
  const previewRef = useRef("");
  previewRef.current = form.imagePreview;
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    []
  );

  const editField = (fieldKey) => {
    const target = FIELD_STEP[fieldKey] || 1;
    setReturnToReview(true);
    goTo(target);
  };

  const handleSubmit = async () => {
    // Re-check every input-bearing step, not just the last one seen — a stepper
    // jump or a Review edit can leave an earlier step incomplete.
    const byStep = [check(1), check(2), check(3)];
    const merged = Object.assign({}, ...byStep);
    if (Object.keys(merged).length > 0) {
      const firstBad = byStep.findIndex((e) => Object.keys(e).length > 0);
      setErrors(merged);
      setStep(firstBad + 1);
      if (firstBad + 1 !== step) pushStep(firstBad + 1);
      return;
    }

    setSubmitting(true);
    try {
      let imageJson = "";

      // Client Rev 3, private league landing: "If no organizer image was uploaded,
      // collapse the image area completely. As the organizer, I chose to have no
      // image. landing page still shows a placeholder image." And: "The image
      // remains an optional enhancement for Private Leagues, not a required part
      // of the layout."
      //
      // Every screen already collapses correctly when a league has no image
      // (getLeagueMedia.hasUploadedMedia). The reason the client still saw one is
      // that their league DID have an image — this flow made it. On create,
      // `needsUpload` was unconditionally true, so with no file picked the line
      // below fell through to `rasterizeIconToPng(form.icon)` and uploaded the
      // variant's own icon as the league's cover art. From the backend's point of
      // view that is an organiser upload, indistinguishable from a real one, and
      // the landing page dutifully rendered it. The organiser chose no image and
      // got one anyway.
      //
      // A PUBLIC league has no picker at all, so "the organiser chose no image"
      // cannot happen there and the rasterised variant icon stays exactly as it
      // is — that is deliberate art for a screen that would otherwise have none.
      // A PRIVATE league has a picker, so an empty one is an answer.
      const isPrivateVariant = variant.gameType === "private";

      // Editing only re-uploads when the organiser actually picked a new file. A
      // league that keeps its art keeps whatever path it already has — rasterising
      // the icon again here would overwrite an uploaded cover with a placeholder.
      const needsUpload = !!form.imageFile || (!isEdit && !isPrivateVariant);

      // …and the other direction: an organiser who REMOVES the cover on a private
      // league. Clearing the picker empties `imagePreview` (it is seeded from the
      // existing cover when the wizard opens), so an empty preview with no new
      // file and a league that still has rows on record means "take it off".
      // Without this the branch below simply re-sent the old path and the image
      // came straight back.
      const existingImages = game?.game_image || [];
      const clearedOnEdit =
        isEdit &&
        isPrivateVariant &&
        !form.imageFile &&
        !form.imagePreview &&
        existingImages.length > 0;

      if (needsUpload) {
        try {
          // A private league uploads what the organiser chose; a public one has no
          // picker, so its variant icon is rasterised to give the league an image.
          const file = form.imageFile || (await rasterizeIconToPng(form.icon));
          const uploadRes = await gameImageUpload([{ blob: file }]);
          const paths = uploadRes?.data?.data;
          if (Array.isArray(paths) && paths.length > 0) {
            imageJson = JSON.stringify(paths.map((path) => ({ image: path })));
          } else {
            toast.error("Icon upload returned no path. Continuing without an icon.");
          }
        } catch (uploadErr) {
          console.error("Icon upload failed:", uploadErr);
          toast.error("We couldn't attach the league icon. Saving anyway.");
        }
      } else if (clearedOnEdit) {
        // GameImage::saveData reads {delete:true, id} as "remove this row", which
        // is the only way to get a league back to having no cover at all.
        imageJson = JSON.stringify(
          existingImages
            .filter((entry) => entry.id)
            .map((entry) => ({ delete: true, id: entry.id }))
        );
      } else if (existingImages.length > 0) {
        imageJson = JSON.stringify(
          existingImages.map((entry) => ({ image: entry.image }))
        );
      }

      const wantsCash = cashEnabled && form.cashLeague;
      const payload = {
        title: form.title.trim(),
        // The one-line hook the preview cards show. Deliberately NOT folded into
        // `description`, which is a separate, much longer field the preview cards
        // never render.
        tagline: form.tagline.trim(),
        // NOTE: `experience_level` has no backend column yet. It is sent so it
        // persists the day one lands. Public leagues only.
        experience_level: variant.showExperienceLevel ? form.experienceLevel : "",
        description: form.description || "",
        start_date: form.startDate,
        end_date: form.endDate,
        reward: variant.showReward ? form.reward || "" : "",
        vanity_url: form.vanityUrl,
        sponsor: "",
        game_type: variant.gameType,
        password: null,
        credit: "",
        join_after_start: form.joinAfterStart,
        private_portfolio: form.privatePortfolio,
        is_cash_prize_league: !!wantsCash,
        cash_prize_amount: wantsCash ? Number(form.cashPrizeAmount) : "",
        // The podium split. An optional placing left blank is sent as "" rather
        // than 0, because the API stores null for it and the landing page drops
        // the row — a 0 would persist as a real "$0 second prize".
        first_place_amount: wantsCash ? Number(form.firstPlaceAmount) : "",
        second_place_amount:
          wantsCash && form.secondPlaceAmount !== "" ? Number(form.secondPlaceAmount) : "",
        third_place_amount:
          wantsCash && form.thirdPlaceAmount !== "" ? Number(form.thirdPlaceAmount) : "",
        asset_category: form.assetCategory,
        // "All" leagues send no ticker list at all — an empty array is how the
        // API reads "the whole category is in play". The picked tickers are kept
        // in form state so switching modes doesn't lose them, so they have to be
        // dropped here rather than cleared on the toggle.
        asset_symbols: form.assetMode === "custom" ? form.assetSymbols || [] : [],
      };
      if (imageJson) {
        payload.image_json = imageJson;
      } else {
        payload.imageJsonString = "";
      }

      if (isEdit) {
        payload.id = game.id;
        // The record keeps whatever these already were — this flow doesn't ask
        // for them, so sending its own blanks would wipe them.
        payload.sponsor = game.sponsor ?? "";
        payload.credit = game.credit ?? "";
        const editRes = await editGame(payload);
        if (editRes?.status !== 200) {
          toast.error("Couldn't save the league. Please try again.");
          return;
        }
        toast.success("League settings saved");
        // The shell caches the league (name, type, membership). Editing it makes
        // that copy wrong, so drop it and let the rail re-read.
        refreshLeagueContext(game.vanity_url);
        // Saved — same as the create path, the draft is done.
        WIZARD_DRAFTS.delete(draftKey);
        navigate(leagueHome);
        return;
      }

      const res = await CreateGames(payload);
      if (res?.status !== 200) {
        toast.error("Couldn't create the league. Please try again.");
        return;
      }

      // NOT named `game`: that is the name of this component's prop, and a
      // `const game` here shadows it across the WHOLE of handleSubmit. Every
      // read of the prop above (the existing cover art, and the entire edit
      // branch) then hit this const's temporal dead zone and threw
      // "Cannot access 'game' before initialization" — which is why saving from
      // League Settings failed every single time while creating worked.
      const created = res?.data?.data || {};
      // No success toast. Client Rev 1: "I would remove the green toast at the
      // top. […] is redundant because the payoff screen already says exactly
      // that. The screen itself should be the confirmation. We need to remove
      // the toast from the public screen 5 payoff screen as well."
      // Both flows end on their own hand-off screen — the code, the link and the
      // way into the league. Same component, different copy; see
      // LeagueCreatedSuccess.
      //
      // The slug and the invite code are separate credentials: the slug is the
      // league's address, the code (private leagues only) is what grants access.
      // The payoff screen shows both, so both go over.
      // `replace` closes the creation flow. Client Rev 1: the payoff's back
      // arrow "must never return to Step 4 or reopen the creation flow" — and
      // for the private flow, "Creation flow is closed and cannot be reopened
      // from Back." Replacing the wizard's history entry with the payoff is what
      // makes that true for the browser's Back button as well as for the arrow.
      // Created. Drop the draft before handing off to the payoff screen.
      WIZARD_DRAFTS.delete(draftKey);
      navigate(variant.createdPath, {
        replace: true,
        state: {
          inviteCode: created.invite_code || "",
          vanityUrl: created.vanity_url || form.vanityUrl,
          title: created.title || form.title,
        },
      });
    } catch (err) {
      console.error(`${isEdit ? "Edit" : "Create"} ${variant.key} league failed:`, err);
      toast.error(
        err?.response?.data?.message ||
          (isEdit
            ? "Couldn't save the league. Please try again."
            : "Couldn't create the league. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Client Rev 4, DESKTOP: "make the entire date field clickable and not just
  // the calendar. You can't put the cursor on a date and edit it manually
  // anyway. On mobile, the entire input field is clickable. Desktop can match
  // it."
  //
  // Chrome only opens a date picker from the native indicator; clicking the
  // text just moves the caret between day/month/year segments. showPicker() is
  // the standard way to open it from anywhere in the field.
  //
  // Desktop ONLY. Mobile already opens the OS picker on any tap, and calling
  // this there would be a second trigger on behaviour the client says is
  // already right. Guarded because showPicker() throws when it is not inside a
  // user gesture, and is missing on older browsers — in either case the field
  // simply behaves the way it does today.
  const openDatePicker = (e) => {
    if (focused) return;
    const el = e.currentTarget;
    if (typeof el.showPicker !== "function") return;
    try {
      el.showPicker();
    } catch (err) {
      /* not user-activated, or unsupported — leave the native behaviour alone */
    }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const isReview = step === STEPS.length;
  const primaryLabel = returnToReview
    ? "Save & Return"
    : isReview
    ? submitting
      ? isEdit
        ? "Saving…"
        : "Creating…"
      : isEdit
      ? "Save changes"
      : variant.submitLabel
    : "Next";

  const primaryButton = (
    <button
      type="button"
      onClick={isReview && !returnToReview ? handleSubmit : handleNext}
      disabled={submitting}
      className="w-full rounded-xl bg-[#0F4146] text-white py-3.5 font-semibold hover:bg-[#0B3A3F] transition-colors disabled:opacity-60"
    >
      {primaryLabel}
    </button>
  );

  return (
    <div
      ref={containerRef}
      className={`min-h-[calc(100vh-6rem)] bg-white ${focused ? "" : "pb-12"}`}
      style={
        focused
          ? { paddingTop: HEADER_HEIGHT, paddingBottom: STICKY_CTA_INSET }
          : undefined
      }
    >
      {/* One back arrow, the centred flow title, nothing else. Fixed, so the
          content — starting with the stepper — scrolls underneath it. */}
      {focused && (
        <WizardHeader title={variant.title} onBack={handleBack} />
      )}
      <div className="max-w-screen-md mx-auto px-4 md:px-8 pt-2">
        {/* DESKTOP title. `!focused` is desktop now (see the note on `focused`),
            so this is the screen's own heading inside the ordinary app shell —
            client Rev 3: "Left-align the screen title and subtitle 'Create Public
            League' - this is a desktop site rule" and "screen titles should be
            left-aligned and share the same starting margin as the content
            block."
            Left alignment is the default; the shared starting margin comes for
            free because the <h1> is the first child of the same
            `max-w-screen-md ... md:px-8` column the form below it uses.
            No back chevron here: "Desktop has the native back button in the
            browser area." The mobile flow keeps its arrow in WizardHeader. */}
        {!focused &&
          (isEdit ? (
            /* Client Rev 5: "The Organizer's League settings link. I like the
               dynamic league title left-aligned as you have it, but let's match
               the exact identity block that we use in the portfolio and watchlist
               screens: same size, color, weight, etc. PRIVATE LEAGUE eyebrow with
               the dynamic league title under it — same identity block."
               So the same COMPONENT the compact league screens render, in the
               same left-aligned column this screen already used. The old pair —
               a 36px "League settings" heading with the league name as a small
               grey line under it — is what that replaces.

               The top margin is a SEPARATE, pre-existing bug this exposed. This
               screen has no top offset of its own: measured at 1440px, its first
               line sat at y=8 while the app's fixed header is 50px tall, so the
               heading was rendering UNDERNEATH the header and only the grey line
               below it was ever visible. (That is why the client's screenshot
               shows no "League settings" title either.) On mobile the wizard's
               own fixed WizardHeader already pads the page, and this block is
               desktop-only, so the fix is desktop-only too. 88px plus the
               column's own pt-2 lands the eyebrow at y=96 — the same line
               Watchlist, Leaderboard and Profile start on. */
            <LeagueIdentityBlock game={game} className="md:mt-[88px]" />
          ) : (
            <h1 className="font-montserrat font-bold text-title-page text-pageTitle">
              {variant.title}
            </h1>
          ))}
        {/* The flow's subtitle introduces the flow, so it belongs to its first
            screen and nowhere else. Client Rev 1, private Screen 2: "remove the
            subtitle line 'Invite your group. Compete privately.' This line only
            belongs on screen 1. remove it from all other screens." The public
            flow's own header spec lists no subtitle either, so both follow the
            same rule — one shared wizard, one behaviour.
            Public still hides it on mobile (those screens go straight to the
            stepper); private shows it at both breakpoints. Per design.
            Editing names the league instead of pitching the flow, and says so on
            every step — it's the only thing identifying which league these
            settings belong to. */}
        {/* Editing: the league name now lives in the identity block above, so this
            line would print it twice. It still carries the flow's lede when
            CREATING, and still names the league on the MOBILE flow, where the
            fixed WizardHeader shows "League settings" and there is no identity
            block. */}
        {((isEdit && focused) || (!isEdit && step === 1)) && (
          <p
            className={`mt-1 text-sm md:text-base text-secondary ${
              // Centred under the centred mobile header; left-aligned on desktop
              // with the title above it (client Rev 3's desktop site rule).
              focused ? "text-center" : "text-left"
            } ${isEdit || variant.showLedeOnMobile ? "" : "hidden md:block"}`}
          >
            {isEdit ? game.title : variant.lede}
          </p>
        )}

        <WizardStepper
          step={step}
          onSelect={requestStep}
          maxReachable={STEPS.length}
        />
        <p className="text-center text-sm text-[#0F4146] mb-5">
          {isEdit ? EDIT_STEP_SUBTITLES[step] : variant.stepSubtitles[step]}
        </p>

        {step === 1 && (
          <div className="space-y-5">
            <Field label="League name" error={errors.title}>
              <TextInput
                type="text"
                value={form.title}
                max={LIMITS.title}
                placeholder="Name your league"
                invalid={!!errors.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    title,
                    vanityUrl: prev.vanityUrlTouched ? prev.vanityUrl : slugify(title),
                  }));
                }}
              />
            </Field>

            <Field label="Tagline" error={errors.tagline}>
              <TextInput
                type="text"
                value={form.tagline}
                max={LIMITS.tagline}
                placeholder={variant.taglinePlaceholder}
                invalid={!!errors.tagline}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, tagline: e.target.value }))
                }
              />
            </Field>

            <Field label="Description" error={errors.description}>
              {/* Rich text, minimal toolbar — the description renders as HTML
                  everywhere else in League Play (hence stripHtml), so this field
                  produces HTML. */}
              <RichTextArea
                value={form.description}
                max={LIMITS.description}
                placeholder={variant.descriptionPlaceholder}
                invalid={!!errors.description}
                onChange={(description) => setForm((prev) => ({ ...prev, description }))}
              />
            </Field>

            {/* Public leagues don't choose art — they still GET an image (the
                variant's default icon, rasterised on submit), the organiser just
                isn't asked to pick one. Private leagues keep the picker until
                their upload lands. */}
            {variant.showIconPicker && (
              <Field label="Choose league icon" error={errors.icon}>
                <IconPicker
                  options={ICON_OPTIONS}
                  value={form.icon}
                  onChange={(icon) => setForm((prev) => ({ ...prev, icon }))}
                />
              </Field>
            )}

            {/* A private league's own art. It becomes the league image, which is
                what its invite page and its cards show. */}
            {variant.showImageUpload && (
              <Field label="Upload a league image" error={errors.imageFile}>
                <ImageUpload
                  file={form.imageFile}
                  preview={form.imagePreview}
                  invalid={!!errors.imageFile}
                  onSelect={pickImage}
                  onClear={clearImage}
                />
              </Field>
            )}

            <Field label="League link" error={errors.vanityUrl}>
              <LinkInput
                prefix={variant.linkPrefix}
                value={form.vanityUrl}
                invalid={!!errors.vanityUrl}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    vanityUrl: slugify(e.target.value),
                    vanityUrlTouched: true,
                  }))
                }
              />
              {vanitySuggestions.length > 0 && (
                <p className="mt-1 text-xs text-secondary">
                  Try: {vanitySuggestions.slice(0, 3).join(", ")}
                </p>
              )}
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {/* THE DATE FIELDS — stacked on mobile, side by side on desktop.
                Client Rev 3: "The date fields are still not lining up correctly.
                Am I doing something wrong? This issue has been on the Rev list
                since Rev1. I do not want this to eat up a ton of time. If you
                have a better solution than the current date template, I am open
                to suggestions." — this is that suggestion, and it is a layout
                change rather than another attempt at the same template.

                Why the previous fixes could not work. Measured in Chrome at a
                390px viewport, which is the client's iPhone 15:

                  content column          358px
                  half track (gap 12)     173px
                  right padding for our
                    calendar icon          40px
                  left padding             14px
                  ⇒ usable for the control 119px
                  native mm/dd/yyyy needs  126px      ← 7px MORE than exists

                So the native control was always ~7px wider than the box it was
                given, at every viewport up to `md`. Rev 1 tried alignment; Rev 2
                added `min-w-0`, which stopped the field from blowing PAST the
                right edge but left it squeezed — the value ends up sitting at a
                different offset in each of the two fields, which is what reads as
                "not lining up". iOS Safari's control is wider than Chrome's, so
                the client's phone is worse than these numbers.

                No amount of padding trimming buys 7px back safely (the icon needs
                its 40px, and the shortfall grows on a 360px phone), so the two
                fields take the full column width instead: 358 - 54 = 304px of
                usable width against 126px needed — 2.4x the room. They also now
                start on the same left edge as every other field in the form,
                which is the alignment the client has been describing.

                Desktop is untouched — "The date fields work fine on the desktop
                version" — where each half track is 344px and fits comfortably. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div className="min-w-0">
            <Field label="Start date" error={errors.startDate}>
              <div className="relative min-w-0">
                <input
                  type="date"
                  value={form.startDate}
                  min={todayStr}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  onClick={openDatePicker}
                  className={`wizard-date-input w-full min-w-0 rounded-xl border bg-white pl-3.5 pr-10 py-3 text-sm focus:outline-none focus:border-[#0F4146] ${
                    errors.startDate ? "border-red-400" : "border-[#E5E7EB]"
                  }`}
                />
                <CalendarIcon className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-[#0F4146] pointer-events-none" />
              </div>
            </Field>
            </div>

            <div className="min-w-0">
            <Field label="End date" error={errors.endDate}>
              <div className="relative min-w-0">
                <input
                  type="date"
                  value={form.endDate}
                  min={form.startDate || todayStr}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  onClick={openDatePicker}
                  className={`wizard-date-input w-full min-w-0 rounded-xl border bg-white pl-3.5 pr-10 py-3 text-sm focus:outline-none focus:border-[#0F4146] ${
                    errors.endDate ? "border-red-400" : "border-[#E5E7EB]"
                  }`}
                />
                <CalendarIcon className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-[#0F4146] pointer-events-none" />
              </div>
            </Field>
            </div>
            </div>

            {/* Experience level — public leagues only. It sets expectations for
                the feed, it doesn't gate entry, which is why the supporting line
                says so out loud. The blurb below follows the selection. */}
            {variant.showExperienceLevel && (
              <Field
                label="Experience level"
                hint="Sets the tone of the league feed. Anyone can join."
              >
                <div className="grid grid-cols-2 gap-3">
                  {EXPERIENCE_OPTIONS.map(({ key, label }) => {
                    const selected = form.experienceLevel === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() =>
                          setForm((prev) => ({ ...prev, experienceLevel: key }))
                        }
                        aria-pressed={selected}
                        className={`rounded-xl bg-white py-3 text-sm transition-all ${
                          selected
                            ? "border-2 border-[#22A36B] font-bold text-[#0F172A]"
                            : "border border-[#E5E7EB] text-secondary hover:border-[#0F4146]"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-sm text-secondary">{noOrphan(experienceBlurb)}</p>
              </Field>
            )}

            <Field label="Asset category">
              {/* All four on one line at every width — they're a single choice,
                  and wrapping them into a 2x2 reads as two pairs. The tile shrinks
                  on a phone instead. */}
              <div className="grid grid-cols-4 gap-2 md:gap-3">
                {ASSET_OPTIONS.map(({ key, label }) => {
                  const Comp = ASSET_ICONS[key];
                  const selected = form.assetCategory === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() =>
                        setForm((prev) =>
                          prev.assetCategory === key
                            ? prev
                            : { ...prev, assetCategory: key, assetSymbols: [] }
                        )
                      }
                      className={`rounded-2xl bg-white flex flex-col items-center justify-center gap-1 md:gap-1.5 py-3 md:py-4 transition-all ${
                        selected
                          ? "border-2 border-[#22A36B] bg-[#F0FBF5]"
                          : "border border-[#E5E7EB] hover:border-[#0F4146]"
                      }`}
                    >
                      <Comp className="w-7 h-7 md:w-8 md:h-8" stroke="#0F4146" />
                      <span className="text-xs md:text-sm font-medium text-[#0F172A]">
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Field>

            <AssetSelector
              category={form.assetCategory}
              mode={form.assetMode}
              onModeChange={(assetMode) => setForm((prev) => ({ ...prev, assetMode }))}
              value={form.assetSymbols}
              onChange={(next) => setForm((prev) => ({ ...prev, assetSymbols: next }))}
              error={errors.assetSymbols}
            />

          </div>
        )}

        {step === 3 && (
          <SettingsStep
            variant={variant}
            form={form}
            setForm={setForm}
            errors={errors}
            cashEnabled={cashEnabled}
            // Both are creation-flow chrome: the league is already running, and
            // its invite code already exists.
            showFairByDesign={!isEdit}
            showInviteHint={!isEdit}
          />
        )}

        {isReview && (
          <InfoCard
            icon={<ClipboardIcon className="w-8 h-8" />}
            title={isEdit ? "Review & save" : "Review & create"}
          >
            {/* Rows on mobile, a two-column grid of cells on desktop — ReviewRow
                renders whichever shape the breakpoint calls for. */}
            <div className="-mt-1 md:mt-1 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-5">
              {/* Exactly the rows the design lists — the summary is a last look at
                  the decisions that shape the league, not a dump of every field.
                  Tagline and Icon are edited on Basics, which the stepper reaches
                  in one click. */}
              {/* Client Rev 1, Screen 4: at the full 28-character title the name
                  and the link derived from it ran off the screen — "it should
                  show an ellipsis, or we should limit the link's character
                  count." Ellipsis, so the organiser still sees the whole thing
                  on the step that owns it (one tap away) rather than losing
                  characters they typed. */}
              <ReviewRow
                label="League name"
                value={form.title}
                clamp
                onEdit={() => editField("title")}
              />
              {/* The tagline is what every league card shows, so the last look
                  before creating should include it. Client Rev 1: "We should
                  probably add the tagline field to both the public and private
                  review and create screens. Use ellipses or something; don't
                  allow text to go outside." */}
              <ReviewRow
                label="Tagline"
                value={form.tagline}
                clamp
                onEdit={() => editField("tagline")}
              />
              <ReviewRow
                label="League link"
                value={form.vanityUrl ? `${variant.linkPrefix}${form.vanityUrl}` : ""}
                tone="link"
                clamp
                onEdit={() => editField("vanityUrl")}
              />
              <ReviewRow
                label="Dates"
                value={formatDateRange(form.startDate, form.endDate)}
                onEdit={() => editField("startDate")}
              />
              {/* Client Rev 1, Screen 4: "Add Experience level to the review
                  summary - The user selected this on Step 2, but it is missing
                  from the review. Put Experience level in the summary before
                  Asset category." Public leagues only — it's the only flow that
                  asks for it. */}
              {variant.showExperienceLevel && (
                <ReviewRow
                  label="Experience level"
                  value={experienceLabel}
                  onEdit={() => editField("experienceLevel")}
                />
              )}
              <ReviewRow
                label="Asset category"
                value={ASSET_LABELS[form.assetCategory]}
                onEdit={() => editField("assetCategory")}
              />
              <ReviewRow
                label="Allowed assets"
                value={
                  form.assetMode === "all"
                    ? // "All Mixed" doesn't read as English — a mixed league is
                      // simply all assets.
                      form.assetCategory === "mixed"
                      ? "All Assets"
                      : `All ${ASSET_LABELS[form.assetCategory] || "Assets"}`
                    : form.assetSymbols?.length
                    ? // Base symbols, per the app-wide instrument-display rule —
                      // "BTC", never "X:BTCUSD".
                      form.assetSymbols.map((t) => baseSymbol(t)).join(", ")
                    : ""
                }
                onEdit={() => editField("assetSymbols")}
              />
              {/* ── The four settings rows, in the order client Rev 3 drew ──
                  Rev 3 gives the private Screen 4 as an explicit two-column
                  table:

                    Left column      Right column
                    League name      Tagline
                    League link      Dates
                    Asset category   Allowed assets
                    Prize            Allow late joins
                    Extra prize      Portfolio visibility

                  and for the public flow: "Move Portfolio visibility under Allow
                  late joins. Fixing the visual balance, and it is also
                  semantically better because those two are both league
                  settings."

                  The desktop grid fills row-major, so position in this list IS
                  position in the table. Public carries an Experience level cell
                  that private does not, which shifts everything by one — so the
                  only way to land BOTH tables is to order Prize and Allow late
                  joins per variant:

                    public   … allowed assets │ allow late joins
                             … prize          │ portfolio visibility   <- "under"
                    private  … prize          │ allow late joins
                             … extra prize    │ portfolio visibility

                  `fullRow` is gone from all three of Prize / Extra prize /
                  Portfolio visibility: Rev 1 had them spanning the grid, and
                  Rev 3's table puts each in a single cell.
                  Both settings still review in their SHORT form — the full
                  sentences from leagueFormat are what the league shows its
                  members once it exists. */}
              {(() => {
                const lateJoins = (
                  <ReviewRow
                    key="lateJoins"
                    label="Allow late joins"
                    value={form.joinAfterStart ? "Yes" : "No"}
                    icon={
                      <span
                        className={`shrink-0 w-1.5 h-1.5 rounded-full ${
                          form.joinAfterStart ? "bg-[#22A36B]" : "bg-[#9CA3AF]"
                        }`}
                      />
                    }
                    onEdit={() => editField("joinAfterStart")}
                  />
                );

                /* Prize — what EVERY league awards, so it is on both flows'
                    summaries. Client Rev 1, private Screen 4: "Add the Prize
                    section above the Extra prize... But you have to remove the
                    chevron because this is not an editable field." Rev 3 now says
                    the same for the public flow, on mobile and desktop: "Screen 4
                    'prize field' is not a field that can be revised. Remove the
                    chevron and make sure it's not clickable." So `editable` is
                    flatly false rather than conditional on the variant —
                    ReviewRow's `disabled` then makes the mobile row inert too, not
                    just chevron-less.
                    Rev 1, Screen 4: the old single line "Top 3 badges · 1st gets
                    1,000 Coins" "wraps weirdly. Make it one on top of the other,
                    right-aligned." */
                const prize = (
                  <ReviewRow
                    key="prize"
                    label="Prize"
                    lines={["Top 3 badges", "1st gets 1,000 QUSHi Coins"]}
                    editable={false}
                  />
                );

                /* Extra prize — whatever a private league's organiser put up on
                    top of that. Optional, so the row disappears when they added
                    nothing. Rev 2: "show the Extra prize value for a maximum of
                    two lines, then truncate with an ellipsis." */
                const extraPrize =
                  variant.showReward && form.reward.trim() ? (
                    <ReviewRow
                      key="extraPrize"
                      label={variant.reviewRewardLabel}
                      value={form.reward}
                      clampLines={2}
                      onEdit={() => editField("reward")}
                    />
                  ) : null;

                const portfolio = (
                  <ReviewRow
                    key="portfolio"
                    label="Portfolio visibility"
                    value={portfolioVisibilityShort(form.privatePortfolio)}
                    icon={
                      form.privatePortfolio ? (
                        <EyeOffIcon className="shrink-0 w-4 h-4" />
                      ) : (
                        <EyeIcon className="shrink-0 w-4 h-4" />
                      )
                    }
                    onEdit={() => editField("privatePortfolio")}
                  />
                );

                return variant.showReward
                  ? [prize, lateJoins, extraPrize, portfolio]
                  : [lateJoins, prize, portfolio];
              })()}

              {cashEnabled && form.cashLeague && (
                <>
                  <ReviewRow
                    label="Total prize pool"
                    value={formatPrize(form.cashPrizeAmount)}
                    fullRow
                    onEdit={() => editField("cashPrizeAmount")}
                  />
                  <ReviewRow
                    label="1st place"
                    value={formatPrize(form.firstPlaceAmount)}
                    fullRow
                    onEdit={() => editField("firstPlaceAmount")}
                  />
                  {/* An optional placing left blank is not shown here either —
                      Review states what the game awards, and a row reading "—"
                      would imply a prize that does not exist. */}
                  {formatPrize(form.secondPlaceAmount) && (
                    <ReviewRow
                      label="2nd place"
                      value={formatPrize(form.secondPlaceAmount)}
                      fullRow
                      onEdit={() => editField("secondPlaceAmount")}
                    />
                  )}
                  {formatPrize(form.thirdPlaceAmount) && (
                    <ReviewRow
                      label="3rd place"
                      value={formatPrize(form.thirdPlaceAmount)}
                      fullRow
                      onEdit={() => editField("thirdPlaceAmount")}
                    />
                  )}
                </>
              )}
            </div>
          </InfoCard>
        )}

        {/* One button, full width, on every step. Going back is the arrow in the
            header and the stepper at both breakpoints — backward jumps are always
            allowed, so a second button here would only compete with the one
            action the step is asking for. */}
        {!focused && <div className="mt-8">{primaryButton}</div>}
      </div>

      {/* Client Rev 1: "Keyboard closed: sticky CTA visible. Keyboard open:
          sticky CTA hidden." The hiding is CSS off body.keyboard-open, which
          AuthLayout already toggles from visualViewport for the whole app — one
          detector, so this can never disagree with the bottom nav's own hiding.
          `md:left-64` clears the desktop sidebar. */}
      {focused && (
        <div className="wizard-sticky-cta fixed bottom-0 left-0 right-0 md:left-64 z-40 bg-white border-t border-[#E5E7EB] px-4 md:px-8 py-3">
          <div className="max-w-screen-md mx-auto">{primaryButton}</div>
        </div>
      )}
    </div>
  );
};

export default LeagueWizard;
